import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Share component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconShare extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M15.8721 5.36671C15.8721 3.84145 17.1085 2.60498 18.6338 2.60498C20.1591 2.60498 21.3955 3.84145 21.3955 5.36671C21.3955 6.89197 20.1591 8.12844 18.6338 8.12844C17.5864 8.12844 16.6752 7.54536 16.207 6.68608C16.196 6.65388 16.1827 6.62206 16.167 6.5908C16.1521 6.56102 16.1354 6.53267 16.1172 6.5058C15.9597 6.15851 15.8721 5.77285 15.8721 5.36671ZM18.6338 9.62844C17.233 9.62844 15.99 8.95257 15.2132 7.90913L9.45746 10.7998C9.56906 11.1807 9.62893 11.5836 9.62893 12.0005C9.62893 12.4172 9.56912 12.8199 9.45763 13.2006L15.2125 16.0909C15.9892 15.0469 17.2325 14.3706 18.6338 14.3706C20.9875 14.3706 22.8955 16.2786 22.8955 18.6323C22.8955 20.986 20.9875 22.8941 18.6338 22.8941C16.2801 22.8941 14.3721 20.986 14.3721 18.6323C14.3721 18.216 14.4318 17.8136 14.5431 17.4332L8.78792 14.5428C8.01112 15.5863 6.76809 16.2622 5.3672 16.2622C3.01351 16.2622 1.10547 14.3542 1.10547 12.0005C1.10547 9.64681 3.01351 7.73877 5.3672 7.73877C6.76788 7.73877 8.01073 8.41449 8.78757 9.45773L14.5434 6.56695C14.4319 6.18626 14.3721 5.78347 14.3721 5.36671C14.3721 3.01302 16.2801 1.10498 18.6338 1.10498C20.9875 1.10498 22.8955 3.01302 22.8955 5.36671C22.8955 7.7204 20.9875 9.62844 18.6338 9.62844ZM7.88308 13.1412C7.8653 13.1676 7.84899 13.1954 7.83434 13.2246C7.81893 13.2553 7.80583 13.2865 7.79496 13.3181C7.3271 14.1783 6.41534 14.7622 5.3672 14.7622C3.84194 14.7622 2.60547 13.5258 2.60547 12.0005C2.60547 10.4752 3.84194 9.23877 5.3672 9.23877C6.4158 9.23877 7.32791 9.82318 7.79558 10.6841C7.80633 10.715 7.81922 10.7457 7.83434 10.7757C7.84874 10.8044 7.86473 10.8318 7.88216 10.8577C8.04065 11.206 8.12893 11.5929 8.12893 12.0005C8.12893 12.4073 8.04099 12.7935 7.88308 13.1412ZM16.1154 17.4971C15.9591 17.8434 15.8721 18.2277 15.8721 18.6323C15.8721 20.1576 17.1085 21.3941 18.6338 21.3941C20.1591 21.3941 21.3955 20.1576 21.3955 18.6323C21.3955 17.1071 20.1591 15.8706 18.6338 15.8706C17.5872 15.8706 16.6767 16.4527 16.2082 17.3109C16.197 17.3442 16.1833 17.3772 16.167 17.4095C16.1516 17.4403 16.1343 17.4695 16.1154 17.4971Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-share') === undefined) {
  customElements.define('cat-icon-share', BlocksIconShare);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-share': BlocksIconShare;
  }
}
